/**
 * PresupuestoCostoHierarchy
 * Componente para mostrar la estructura jerárquica de un presupuesto de costo
 * Muestra: Áreas → Partidas → Recursos con costos
 */

import React, { useState, useEffect } from 'react';
import { FiChevronDown, FiChevronRight, FiPlus, FiEdit2, FiTrash2, FiCheck, FiX } from 'react-icons/fi';
import presupuestoHierarchyService from '../services/presupuestoHierarchyService';
import { presupuestoCostoService } from '../services/onebaseService';
import AgregarPartidaPresupuestoModal from './AgregarPartidaPresupuestoModal';
import AgregarRecursoPresupuestoModal from './AgregarRecursoPresupuestoModal';
import EditarRecursoPresupuestoModal from './EditarRecursoPresupuestoModal';
import EditarPartidaPresupuestoModal from './EditarPartidaPresupuestoModal';
import EditarCantidadPartidaModal from './EditarCantidadPartidaModal';
import CrearOrdenCompraOneBaseModal from './CrearOrdenCompraOneBaseModal';
import { toast } from 'react-toastify';
import './PresupuestoVentaHierarchy.css';

import Loading from './Loading';
const PresupuestoCostoHierarchy = ({ presupuestoCostoId, cuentaId, onUpdate }) => {
  const [areas, setAreas] = useState([]);
  const [expandedAreas, setExpandedAreas] = useState({});
  const [expandedPartidas, setExpandedPartidas] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAgregarPartidaModal, setShowAgregarPartidaModal] = useState(false);
  const [selectedAreaForPartida, setSelectedAreaForPartida] = useState(null);
  const [selectedMaestroAreaForPartida, setSelectedMaestroAreaForPartida] = useState(null);
  const [showAgregarRecursoModal, setShowAgregarRecursoModal] = useState(false);
  const [selectedPartidaForRecurso, setSelectedPartidaForRecurso] = useState(null);
  const [showEditRecursoModal, setShowEditRecursoModal] = useState(false);
  const [selectedRecursoForEdit, setSelectedRecursoForEdit] = useState(null);
  const [showEditPartidaModal, setShowEditPartidaModal] = useState(false);
  const [selectedPartidaForEdit, setSelectedPartidaForEdit] = useState(null);
  const [showEditCantidadPartidaModal, setShowEditCantidadPartidaModal] = useState(false);
  const [selectedPartidaForCantidad, setSelectedPartidaForCantidad] = useState(null);

  // Inline editing states
  const [editingRecursoId, setEditingRecursoId] = useState(null);
  const [editingRecursoData, setEditingRecursoData] = useState({});
  const [editingPartidaId, setEditingPartidaId] = useState(null);
  const [editingPartidaData, setEditingPartidaData] = useState({});

  // Selección de recursos para OC
  const [modoSeleccionRecursos, setModoSeleccionRecursos] = useState(false);
  const [recursosSeleccionados, setRecursosSeleccionados] = useState(new Set());
  const [showCrearOCModal, setShowCrearOCModal] = useState(false);
  const [presupuestoCostoData, setPresupuestoCostoData] = useState(null);

  // Load areas when component mounts or presupuesto changes
  useEffect(() => {
    if (presupuestoCostoId) {
      loadAreas();
    }
  }, [presupuestoCostoId]);

  const loadAreas = async () => {
    setLoading(true);
    try {
      // Fetch presupuesto data for modal context
      const presupuestoResponse = await presupuestoCostoService.getById(presupuestoCostoId);
      const presupuestoData = presupuestoResponse.data;
      setPresupuestoCostoData(presupuestoData);

      // Fetch areas with all nested data (partidas and recursos) in one request
      // El backend ya devuelve toda la jerarquía con prefetch_related optimizado
      const areasResponse = await presupuestoHierarchyService.getCostoAreasForPresupuesto(presupuestoCostoId);
      const areasData = Array.isArray(areasResponse.data) ? areasResponse.data : (areasResponse.data.results || []);

      // Los datos ya vienen completos con partidas_estructura y recursos_estructura anidados
      const areasWithPartidas = areasData.map((area) => {
        const partidas = area.partidas_estructura || [];

        const partidasWithRecursos = partidas.map((partida) => {
          const recursos = partida.recursos_estructura || [];

          // Calcular subtotales de partida
          const subtotal_venta = recursos.reduce((sum, r) => sum + (parseFloat(r.subtotal || 0)), 0);
          const subtotal_costo = recursos.reduce((sum, r) => sum + (parseFloat(r.subtotal_costo || 0)), 0);
          const subtotal_venta_partida = parseFloat(partida.cantidad || 1) * subtotal_venta;
          const subtotal_costo_partida = parseFloat(partida.cantidad || 1) * subtotal_costo;

          return {
            ...partida,
            recursos_estructura: recursos,
            subtotal_venta,
            subtotal_costo,
            subtotal_venta_partida,
            subtotal_costo_partida
          };
        });

        return {
          ...area,
          partidas: partidasWithRecursos  // Cambio: usar 'partidas' en lugar de 'partidas_estructura'
        };
      });

      setAreas(areasWithPartidas);
    } catch (error) {
      console.error('Error loading areas:', error);
      toast.error('Error al cargar las áreas del presupuesto');
    } finally {
      setLoading(false);
    }
  };

  const toggleAreaExpanded = (areaId) => {
    setExpandedAreas(prev => ({
      ...prev,
      [areaId]: !prev[areaId]
    }));
  };

  const togglePartidaExpanded = (partidaId) => {
    setExpandedPartidas(prev => ({
      ...prev,
      [partidaId]: !prev[partidaId]
    }));
  };

  const deleteArea = async (areaId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta área del presupuesto?')) {
      return;
    }

    try {
      // Delete area assignment from presupuesto (not from catalog)
      await presupuestoHierarchyService.deleteCostoArea(areaId);
      toast.success('Área eliminada del presupuesto');
      loadAreas();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting area:', error);
      toast.error('Error al eliminar el área');
    }
  };

  const deletePartida = async (partidaId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta partida?')) {
      return;
    }

    try {
      // Delete partida from the master catalog
      await presupuestoHierarchyService.deleteMaestroPartida(partidaId);
      toast.success('Partida eliminada');
      loadAreas();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting partida:', error);
      toast.error('Error al eliminar la partida');
    }
  };

  const deleteRecurso = async (recursoId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este recurso?')) {
      return;
    }

    try {
      // Delete recurso from the presupuesto costo structure
      await presupuestoHierarchyService.deleteCostoRecurso(recursoId);
      toast.success('Recurso eliminado');
      loadAreas();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting recurso:', error);
      toast.error('Error al eliminar el recurso');
    }
  };

  const startEditingRecurso = (recurso) => {
    setEditingRecursoId(recurso.id);
    setEditingRecursoData({ ...recurso });
  };

  const calculatePrecioVenta = (data) => {
    const costo = parseFloat(data.precio_unitario_costo || 0);
    const markup = parseFloat(data.markup || 0);
    return costo * (1 + markup / 100);
  };

  const saveRecurso = async () => {
    try {
      // Calculate precio_unitario_venta automatically
      const dataToSave = {
        ...editingRecursoData,
        precio_unitario_venta: calculatePrecioVenta(editingRecursoData)
      };
      // Update recurso in the presupuesto (PresupuestoCostoRecurso table)
      await presupuestoHierarchyService.updateCostoRecurso(editingRecursoId, dataToSave);
      toast.success('Recurso actualizado');

      // Update the areas state with the new recurso data and recalculate subtotals
      setAreas(prevAreas =>
        prevAreas.map(area => ({
          ...area,
          partidas: area.partidas.map(partida => {
            // Update recursos and recalculate partida subtotals
            const updatedRecursos = partida.recursos_estructura.map(recurso =>
              recurso.id === editingRecursoId ? dataToSave : recurso
            );

            // Recalculate partida subtotals
            const newSubtotalVenta = updatedRecursos.reduce((sum, r) => sum + (parseFloat(r.subtotal || 0)), 0);
            const newSubtotalCosto = updatedRecursos.reduce((sum, r) => sum + (parseFloat(r.subtotal_costo || 0)), 0);
            const newSubtotalVentaPartida = parseFloat(partida.cantidad || 1) * newSubtotalVenta;
            const newSubtotalCostoPartida = parseFloat(partida.cantidad || 1) * newSubtotalCosto;

            return {
              ...partida,
              recursos_estructura: updatedRecursos,
              subtotal_venta: newSubtotalVenta,
              subtotal_costo: newSubtotalCosto,
              subtotal_venta_partida: newSubtotalVentaPartida,
              subtotal_costo_partida: newSubtotalCostoPartida
            };
          })
        })).map(area => ({
          ...area,
          // Recalculate area subtotals
          subtotal_venta: area.partidas.reduce((sum, p) => sum + (parseFloat(p.subtotal_venta_partida || 0)), 0),
          subtotal_costo: area.partidas.reduce((sum, p) => sum + (parseFloat(p.subtotal_costo_partida || 0)), 0)
        }))
      );

      setEditingRecursoId(null);
      setEditingRecursoData({});
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating recurso:', error);
      toast.error('Error al actualizar el recurso');
    }
  };

  const cancelEditRecurso = () => {
    setEditingRecursoId(null);
    setEditingRecursoData({});
  };

  const startEditingPartida = (partida) => {
    setEditingPartidaId(partida.id);
    setEditingPartidaData({ ...partida });
  };

  const savePartida = async () => {
    try {
      // Update partida in the presupuesto (independent from master catalog)
      await presupuestoHierarchyService.updateCostoPartida(editingPartidaId, editingPartidaData);
      toast.success('Partida actualizada');

      // Update the areas state with the new partida data and recalculate area subtotals
      setAreas(prevAreas =>
        prevAreas.map(area => {
          // Update partida and recalculate subtotals
          const updatedPartidas = area.partidas.map(partida => {
            if (partida.id === editingPartidaId) {
              // Recalculate partida subtotals based on its resources
              const newSubtotalVenta = partida.recursos_estructura.reduce((sum, r) => sum + (parseFloat(r.subtotal || 0)), 0);
              const newSubtotalCosto = partida.recursos_estructura.reduce((sum, r) => sum + (parseFloat(r.subtotal_costo || 0)), 0);
              const newSubtotalVentaPartida = parseFloat(editingPartidaData.cantidad || 1) * newSubtotalVenta;
              const newSubtotalCostoPartida = parseFloat(editingPartidaData.cantidad || 1) * newSubtotalCosto;

              return {
                ...editingPartidaData,
                recursos_estructura: partida.recursos_estructura,
                subtotal_venta: newSubtotalVenta,
                subtotal_costo: newSubtotalCosto,
                subtotal_venta_partida: newSubtotalVentaPartida,
                subtotal_costo_partida: newSubtotalCostoPartida
              };
            }
            return partida;
          });

          // Recalculate area subtotals
          return {
            ...area,
            partidas: updatedPartidas,
            subtotal_venta: updatedPartidas.reduce((sum, p) => sum + (parseFloat(p.subtotal_venta_partida || 0)), 0),
            subtotal_costo: updatedPartidas.reduce((sum, p) => sum + (parseFloat(p.subtotal_costo_partida || 0)), 0)
          };
        })
      );

      setEditingPartidaId(null);
      setEditingPartidaData({});
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating partida:', error);
      toast.error('Error al actualizar la partida');
    }
  };

  const cancelEditPartida = () => {
    setEditingPartidaId(null);
    setEditingPartidaData({});
  };

  // Funciones para seleccionar recursos
  const toggleRecursoSeleccionado = (recursoId) => {
    setRecursosSeleccionados(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recursoId)) {
        newSet.delete(recursoId);
      } else {
        newSet.add(recursoId);
      }
      return newSet;
    });
  };

  const entrarModoSeleccion = () => {
    setModoSeleccionRecursos(true);
    setRecursosSeleccionados(new Set());
  };

  const salirModoSeleccion = () => {
    setModoSeleccionRecursos(false);
    setRecursosSeleccionados(new Set());
  };

  const abrirCrearOC = () => {
    if (recursosSeleccionados.size === 0) {
      toast.warning('Selecciona al menos un recurso');
      return;
    }
    setShowCrearOCModal(true);
  };

  // Obtener los recursos seleccionados con su información completa
  const getRecursosSeleccionadosData = () => {
    const recursos = [];
    areas.forEach(area => {
      area.partidas?.forEach(partida => {
        partida.recursos_estructura?.forEach(recurso => {
          if (recursosSeleccionados.has(recurso.id)) {
            recursos.push({
              ...recurso,
              area_nombre: area.nombre,
              partida_nombre: partida.nombre,
              partida_id: partida.id,
              area_id: area.id
            });
          }
        });
      });
    });
    return recursos;
  };

  if (loading) {
    return <Loading fullScreen={false} size="sm" mensaje="Cargando estructura" />;
  }

  if (areas.length === 0) {
    return (
      <div className="presupuesto-hierarchy-empty">
        <p>No hay áreas asignadas a este presupuesto</p>
        <p className="hint">Ve al catálogo maestro para asignar áreas a este presupuesto</p>
      </div>
    );
  }

  return (
    <>
      <div className="presupuesto-hierarchy">
        <div className="hierarchy-header">
          <div>
            <h3>Estructura del Presupuesto</h3>
            <p className="hint">Áreas → Partidas → Recursos</p>
          </div>
          <div className="header-actions">
            {!modoSeleccionRecursos ? (
              <button
                className="btn-primary btn-sm"
                onClick={entrarModoSeleccion}
                title="Seleccionar recursos para crear OC"
              >
                Seleccionar Recursos
              </button>
            ) : (
              <>
                <button
                  className="btn-success btn-sm"
                  onClick={abrirCrearOC}
                  disabled={recursosSeleccionados.size === 0}
                  title={recursosSeleccionados.size === 0 ? "Selecciona al menos un recurso" : "Crear OC"}
                >
                  Solicitar OC ({recursosSeleccionados.size})
                </button>
                <button
                  className="btn-secondary btn-sm"
                  onClick={salirModoSeleccion}
                  title="Cancelar selección"
                >
                  Cancelar
                </button>
              </>
            )}
          </div>
        </div>

        <div className="areas-list">
        {areas.map(area => (
          <div key={area.id} className="area-item">
            <div className="area-header">
              <span
                className="toggle-icon"
                onClick={() => toggleAreaExpanded(area.id)}
              >
                {expandedAreas[area.id] ? <FiChevronDown /> : <FiChevronRight />}
              </span>
              <span
                className="area-name"
                onClick={() => toggleAreaExpanded(area.id)}
              >
                {area.nombre || 'Sin nombre'}
              </span>
              <span
                className="area-subtotal-costo"
                onClick={() => toggleAreaExpanded(area.id)}
              >
                Costo: ${(parseFloat(area.subtotal_costo) || 0).toLocaleString('es-CL', {maximumFractionDigits: 0})}
              </span>
              <div className="area-actions" onClick={(e) => e.stopPropagation()}>
                <button
                  className="delete-btn"
                  onClick={() => deleteArea(area.id)}
                  title="Eliminar área del presupuesto"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>

            {expandedAreas[area.id] && (
              <div className="area-content">
                <button
                  className="add-partida-btn"
                  onClick={() => {
                    setSelectedAreaForPartida(area.id);
                    setSelectedMaestroAreaForPartida(area.area); // Master Area ID from PresupuestoCostoArea
                    setShowAgregarPartidaModal(true);
                  }}
                  title="Agregar partida"
                >
                  <FiPlus /> Agregar Partida
                </button>

                {area.partidas && area.partidas.length > 0 ? (
                  <div className="partidas-list">
                    {area.partidas.map(partida => (
                      <div key={partida.id} className="partida-item">
                        {editingPartidaId === partida.id ? (
                          <div className="partida-header-wrapper editing">
                            <input
                              type="text"
                              className="partida-input"
                              value={editingPartidaData.nombre || ''}
                              onChange={(e) => setEditingPartidaData({...editingPartidaData, nombre: e.target.value})}
                              placeholder="Nombre de partida"
                            />
                            <input
                              type="number"
                              className="partida-input-qty"
                              value={editingPartidaData.cantidad || 0}
                              onChange={(e) => setEditingPartidaData({...editingPartidaData, cantidad: parseFloat(e.target.value) || 0})}
                              placeholder="Cantidad"
                              step="0.01"
                            />
                            <div className="partida-actions">
                              <button
                                className="save-btn"
                                onClick={savePartida}
                                title="Guardar"
                              >
                                ✓
                              </button>
                              <button
                                className="cancel-btn"
                                onClick={cancelEditPartida}
                                title="Cancelar"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="partida-header-wrapper">
                            <div
                              className="partida-header"
                              onClick={() => togglePartidaExpanded(partida.id)}
                            >
                              <span className="toggle-icon">
                                {expandedPartidas[partida.id] ? <FiChevronDown /> : <FiChevronRight />}
                              </span>
                              <span className="partida-name">{partida.nombre}</span>
                              <span className="partida-cantidad">
                                Cantidad: {parseFloat(partida.cantidad || 0).toLocaleString('es-CL')}
                              </span>
                              <span className="partida-subtotal-costo">
                                Costo: ${(parseFloat(partida.subtotal_costo) || 0).toLocaleString('es-CL', {maximumFractionDigits: 0})}
                              </span>
                              <span className="partida-total">
                                Total: ${(parseFloat(partida.subtotal_costo_partida) || 0).toLocaleString('es-CL', {maximumFractionDigits: 0})}
                              </span>
                              {/* Ejecutado con barra de progreso */}
                              <div className="partida-ejecutado-container">
                                <span className="partida-ejecutado-label" style={{
                                  color: parseFloat(partida.porcentaje_avance || 0) > 80 ? '#d9534f' : parseFloat(partida.porcentaje_avance || 0) > 50 ? '#f0ad4e' : '#5cb85c',
                                  fontWeight: 'bold'
                                }}>
                                  💰 {parseFloat(partida.cantidad_ejecutada || 0).toLocaleString('es-CL')}
                                </span>
                                <div className="partida-progress-bar-inline">
                                  <div className="partida-progress-bar-wrapper-inline">
                                    <div
                                      className="partida-progress-bar-inline-fill"
                                      style={{
                                        width: `${Math.min(parseFloat(partida.porcentaje_avance || 0), 100)}%`,
                                        backgroundColor: parseFloat(partida.porcentaje_avance || 0) > 80 ? '#d9534f' : parseFloat(partida.porcentaje_avance || 0) > 50 ? '#f0ad4e' : '#5cb85c'
                                      }}
                                    />
                                  </div>
                                  <span className="partida-progress-text-inline">{parseFloat(partida.porcentaje_avance || 0).toLocaleString('es-CL', {maximumFractionDigits: 1})}%</span>
                                </div>
                              </div>
                            </div>
                            <div className="partida-actions">
                              <button
                                className="edit-btn"
                                onClick={() => {
                                  setSelectedPartidaForCantidad(partida);
                                  setShowEditCantidadPartidaModal(true);
                                }}
                                title="Editar cantidad de la partida"
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                className="delete-btn"
                                onClick={() => deletePartida(partida.id)}
                                title="Eliminar partida"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                        )}

                        {expandedPartidas[partida.id] && partida.recursos_estructura && (
                          <div className="partida-content">
                            <button
                              className="add-recurso-btn"
                              onClick={() => {
                                setSelectedPartidaForRecurso(partida.id);
                                setShowAgregarRecursoModal(true);
                              }}
                              title="Agregar recurso"
                            >
                              <FiPlus /> Agregar Recurso
                            </button>

                            {partida.recursos_estructura.length > 0 ? (
                              <div className="recursos-list">
                                {partida.recursos_estructura.map(recurso => (
                                  <div key={recurso.id} className="recurso-item">
                                    {editingRecursoId === recurso.id ? (
                                      <div className="recurso-details editing">
                                        <input
                                          type="text"
                                          className="recurso-input"
                                          value={editingRecursoData.nombre || ''}
                                          onChange={(e) => setEditingRecursoData({...editingRecursoData, nombre: e.target.value})}
                                          placeholder="Nombre"
                                        />
                                        <input
                                          type="number"
                                          className="recurso-input"
                                          value={editingRecursoData.cantidad_presupuesto || 0}
                                          onChange={(e) => setEditingRecursoData({...editingRecursoData, cantidad_presupuesto: e.target.value})}
                                          placeholder="Cantidad"
                                          step="0.01"
                                        />
                                        <input
                                          type="number"
                                          className="recurso-input"
                                          value={editingRecursoData.precio_unitario_costo || 0}
                                          onChange={(e) => setEditingRecursoData({...editingRecursoData, precio_unitario_costo: e.target.value})}
                                          placeholder="Costo"
                                          step="0.01"
                                        />
                                        <div className="recurso-actions">
                                          <button
                                            className="save-btn"
                                            onClick={saveRecurso}
                                            title="Guardar"
                                          >
                                            ✓
                                          </button>
                                          <button
                                            className="cancel-btn"
                                            onClick={cancelEditRecurso}
                                            title="Cancelar"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        {modoSeleccionRecursos && (
                                          <input
                                            type="checkbox"
                                            className="recurso-checkbox"
                                            checked={recursosSeleccionados.has(recurso.id)}
                                            onChange={() => toggleRecursoSeleccionado(recurso.id)}
                                            style={{ marginRight: '10px' }}
                                          />
                                        )}
                                        <div className="recurso-details">
                                          <span className="recurso-nombre">{recurso.nombre}</span>
                                          <span className="recurso-cantidad">
                                            Presupuestada: {parseFloat(recurso.cantidad_presupuestada_costo || recurso.cantidad_presupuesto || 0).toLocaleString('es-CL')} | Solicitada OC: {parseFloat(recurso.cantidad_solicitada || 0).toLocaleString('es-CL')} | Disponible: {parseFloat(recurso.cantidad_disponible || 0).toLocaleString('es-CL')}
                                          </span>
                                          <span className="recurso-precio-costo">
                                            Costo Unitario: ${(parseFloat(recurso.precio_unitario_costo) || 0).toLocaleString('es-CL', {maximumFractionDigits: 0})}
                                          </span>
                                          <span className="recurso-subtotal-costo">
                                            Total Presupuestado: ${(parseFloat(recurso.subtotal_costo_partida) || 0).toLocaleString('es-CL', {maximumFractionDigits: 0})}
                                          </span>
                                          <div className="recurso-ejecutado-container">
                                            <span className="recurso-subtotal-ejecutado" style={{
                                              color: parseFloat(recurso.porcentaje_ejecucion || 0) > 80 ? '#d9534f' : parseFloat(recurso.porcentaje_ejecucion || 0) > 50 ? '#f0ad4e' : '#5cb85c',
                                              fontWeight: 'bold'
                                            }}>
                                              💰 Total Ejecutado: ${(parseFloat(recurso.total_ejecutado_costo) || 0).toLocaleString('es-CL', {maximumFractionDigits: 0})}
                                            </span>
                                            <div className="recurso-progress-bar-inline">
                                              <div className="recurso-progress-bar-wrapper-inline">
                                                <div
                                                  className="recurso-progress-bar-inline-fill"
                                                  style={{
                                                    width: `${Math.min(parseFloat(recurso.porcentaje_ejecucion || 0), 100)}%`,
                                                    backgroundColor: parseFloat(recurso.porcentaje_ejecucion || 0) > 80 ? '#d9534f' : parseFloat(recurso.porcentaje_ejecucion || 0) > 50 ? '#f0ad4e' : '#5cb85c'
                                                  }}
                                                />
                                              </div>
                                              <span className="recurso-progress-text-inline">{parseFloat(recurso.porcentaje_ejecucion || 0).toLocaleString('es-CL', {maximumFractionDigits: 1})}%</span>
                                            </div>
                                          </div>
                                          <span className="recurso-subtotal-gastado" style={{
                                            color: '#ff9800',
                                            fontWeight: 'bold'
                                          }}>
                                            💸 Total Gastado: ${(parseFloat(recurso.total_gastado_costo) || 0).toLocaleString('es-CL', {maximumFractionDigits: 0})}
                                          </span>
                                        </div>
                                        {!modoSeleccionRecursos && (
                                          <div className="recurso-actions">
                                            <button
                                              className="edit-btn"
                                              onClick={() => startEditingRecurso(recurso)}
                                              title="Editar recurso"
                                            >
                                              <FiEdit2 />
                                            </button>
                                            <button
                                              className="delete-btn"
                                              onClick={() => deleteRecurso(recurso.id)}
                                              title="Eliminar recurso"
                                            >
                                              <FiTrash2 />
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="no-recursos">Sin recursos</div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-partidas">Sin partidas</div>
                )}
              </div>
            )}
          </div>
        ))}
        </div>
      </div>

      {showAgregarPartidaModal && selectedAreaForPartida && (
        <AgregarPartidaPresupuestoModal
          isOpen={showAgregarPartidaModal}
          areaId={selectedAreaForPartida}
          maestroAreaId={selectedMaestroAreaForPartida}
          tipo="costo"
          onClose={() => {
            setShowAgregarPartidaModal(false);
            setSelectedAreaForPartida(null);
            setSelectedMaestroAreaForPartida(null);
          }}
          onSuccess={() => {
            loadAreas();
            if (onUpdate) onUpdate();
          }}
        />
      )}

      {showAgregarRecursoModal && selectedPartidaForRecurso && (
        <AgregarRecursoPresupuestoModal
          isOpen={showAgregarRecursoModal}
          partidaId={selectedPartidaForRecurso}
          tipo="costo"
          onClose={() => {
            setShowAgregarRecursoModal(false);
            setSelectedPartidaForRecurso(null);
          }}
          onSuccess={() => {
            loadAreas();
            if (onUpdate) onUpdate();
          }}
        />
      )}

      {showEditRecursoModal && selectedRecursoForEdit && (
        <EditarRecursoPresupuestoModal
          recurso={selectedRecursoForEdit}
          onClose={() => {
            setShowEditRecursoModal(false);
            setSelectedRecursoForEdit(null);
          }}
          onSuccess={() => {
            loadAreas();
            if (onUpdate) onUpdate();
          }}
        />
      )}

      {showEditPartidaModal && selectedPartidaForEdit && (
        <EditarPartidaPresupuestoModal
          partida={selectedPartidaForEdit}
          onClose={() => {
            setShowEditPartidaModal(false);
            setSelectedPartidaForEdit(null);
          }}
          onSuccess={() => {
            loadAreas();
            if (onUpdate) onUpdate();
          }}
        />
      )}

      {showEditCantidadPartidaModal && selectedPartidaForCantidad && (
        <EditarCantidadPartidaModal
          partida={selectedPartidaForCantidad}
          onClose={() => {
            setShowEditCantidadPartidaModal(false);
            setSelectedPartidaForCantidad(null);
          }}
          onSuccess={() => {
            loadAreas();
            if (onUpdate) onUpdate();
          }}
        />
      )}

      {showCrearOCModal && presupuestoCostoData && (
        <CrearOrdenCompraOneBaseModal
          presupuesto={presupuestoCostoData}
          itemsSeleccionados={getRecursosSeleccionadosData()}
          onClose={() => {
            setShowCrearOCModal(false);
            salirModoSeleccion();
          }}
          onCreated={async () => {
            setShowCrearOCModal(false);
            salirModoSeleccion();
            // Pequeño delay para asegurar que los signals se procesen en backend
            await new Promise(resolve => setTimeout(resolve, 500));
            loadAreas();
            if (onUpdate) onUpdate();
            toast.success('Orden de compra creada exitosamente');
          }}
          key="modal-oc"
        />
      )}
    </>
  );
};

export default PresupuestoCostoHierarchy;
